import React, { memo, useCallback, useEffect, useState } from "react";
import { isMobileOnly } from "react-device-detect";
import { Map } from "react-map-gl/mapbox";
import ScrollToTop from "./common/ScrollToTop";
import { FiChevronUp } from "react-icons/fi";
import AudioPlayer from "react-h5-audio-player";
import slugify from "slugify";
import GifPlayer from "./common/GifPlayer";
import type {
  DocumentEntity,
  VideoEntity,
  VimeoVideo,
  Resources,
} from "../types/strapi";
import { accessToken, siteUrl } from "../utils/constants";
import { getFileExtension } from "../utils/getFileExtension";
import {
  getArchiveDetailFields,
  Datasheet,
} from "../utils/getArchiveDetailFields";
import { isOddAndInRange } from "../utils/archiveDetailsUtils";
import { imgSize } from "../utils/funcs";
import Header from "./common/Header";
import Breadcrumb from "./common/Breadcrumb";
import Footer from "./common/FooterTwo";
import PageHelmet from "./common/Helmet";
import Image from "./objects/Image";
import ImagesCarouselTwo from "./objects/ImagesCarouselTwo";
import VideoStandard from "./objects/VideoStandard";
import DataItem from "./metaData/DataItem";
import "react-h5-audio-player/lib/styles.css";
import { mediaUrl } from "../utils/imageUtils";

interface ArchiveDetailsProps {
  archiveCollectionName?: string | null;
  archiveCollectionItemId?: string | null;
  documents?: DocumentEntity[];
  setArchiveCollectionName?: (name: string | null) => void;
  setRedirectToArchive?: (redirect: boolean) => void;
  urlSlug?: string;
  videos?: VideoEntity[];
  videosVimeo?: VimeoVideo[];
}

const renderDataItem = (
  heading: string,
  text: string | null,
  resources: Resources[] | null | undefined,
  colSize = 4,
  index: number
) => (
  <div key={heading + index} className={`col-lg-${colSize}`}>
    <div className="port-view mt--30">
      <DataItem heading={heading} text={String(text)} resources={resources} />
    </div>
  </div>
);

const ArchiveDetails: React.FC<ArchiveDetailsProps> = ({
  archiveCollectionName,
  archiveCollectionItemId,
  documents = [],
  setArchiveCollectionName,
  setRedirectToArchive,
  urlSlug,
  videos = [],
  videosVimeo = [],
}) => {
  const section = "Archive";
  const [datasheet, setDatasheet] = useState<Datasheet>({
    id: 0,
    title: "",
    url: "",
    contentType: undefined,
    file: [],
    showcase: { name: "" },
    fileType: "",
    img: "",
  });

  const [viewport, setViewport] = useState<{
    latitude?: number;
    longitude?: number;
    zoom?: number;
  }>({});
  const [videosVimeoObject, setVideosVimeoObject] = useState<
    Record<string, string>
  >({});

  const handleMergedCollection = useCallback(() => {
    let mergedCollection: (DocumentEntity | VideoEntity)[] = [];

    if (
      Array.isArray(documents) &&
      documents.length > 0 &&
      Array.isArray(videos) &&
      videos.length > 0
    ) {
      if (!archiveCollectionName || mergedCollection.length === 0) {
        mergedCollection = [...documents, ...videos];
      }

      if (archiveCollectionName === "video") {
        mergedCollection = videos;
      }

      if (archiveCollectionName === "documents") {
        mergedCollection = documents;
      }
    }

    return mergedCollection;
  }, [archiveCollectionName, documents, videos]);

  const findCollectionItem = useCallback(
    (mergedCollection: (DocumentEntity | VideoEntity)[]) => {
      let collectionItem: DocumentEntity | VideoEntity | undefined;
      if (archiveCollectionItemId) {
        collectionItem = mergedCollection.find(
          (item) => item.documentId === archiveCollectionItemId
        );
      } else {
        collectionItem = mergedCollection.find(
          (item) =>
            slugify(item.contentType.title, {
              lower: true,
              strict: true,
              trim: true,
            }) === urlSlug
        );

        if (!collectionItem) {
          setRedirectToArchive?.(true);
        } else {
          const collectionType =
            "fileType" in collectionItem &&
            collectionItem.fileType
              ? "document"
              : "video";
          setArchiveCollectionName?.(collectionType);
        }
      }
      return collectionItem;
    },
    [
      archiveCollectionItemId,
      urlSlug,
      setRedirectToArchive,
      setArchiveCollectionName,
    ]
  );

  const handleArchiveDetails = useCallback(() => {
    const mergedCollection = handleMergedCollection();
    if (documents.length > 0 && mergedCollection.length > 0) {
      const collectionItem = findCollectionItem(mergedCollection);
      if (collectionItem) {
        const dataToSet = {
          ...collectionItem.contentType,
          ...collectionItem,
          id: collectionItem.id,
          file:
            "image" in collectionItem &&
            collectionItem.image &&
            typeof collectionItem.image === "object"
              ? [collectionItem.image]
              : ("file" in collectionItem ? collectionItem.file : undefined) ?? [],
        };

        setDatasheet(dataToSet as Datasheet);

        const viewportObj = {
          latitude:
            collectionItem?.contentType?.latitude ?? -3.9242781,
          longitude:
            collectionItem?.contentType?.longitude ?? -75.0656716,
          zoom:
            collectionItem?.contentType?.latitude &&
            collectionItem?.contentType?.longitude
              ? collectionItem?.contentType?.zoom ?? 3
              : 3,
        };

        setViewport(viewportObj);
      }
    }
  }, [documents, handleMergedCollection, findCollectionItem]);

  const handleVideosVimeo = useCallback(() => {
    if (Array.isArray(videosVimeo) && videosVimeo.length >= 1) {
      const imgIndex = isMobileOnly ? 4 : 5;

      setVideosVimeoObject(
        videosVimeo.reduce((acc, video) => {
          const videoId = video.uri.replace("/videos/", "");
          const thumbnailLink = video.pictures.sizes[imgIndex]?.link;

          if (thumbnailLink) {
            acc[videoId] = thumbnailLink;
          }

          return acc;
        }, {} as Record<string, string>)
      );
    }
  }, [videosVimeo]);

  useEffect(() => {
    handleArchiveDetails();
    handleVideosVimeo();

    const breadcrumbElement = document.querySelector(".breadcrumb-area");
    if (breadcrumbElement) {
      const position =
        breadcrumbElement.getBoundingClientRect().bottom + window.scrollY - 65;
      window.scrollTo({ top: position, behavior: "smooth" });
    }
  }, [handleArchiveDetails, handleVideosVimeo]);

  let counter = 0;

  return (
    <>
      <PageHelmet pageTitle="Portfolio Details" />

      <Header />

      <Breadcrumb backgroundSection={section} title={section} />

      <div className="rn-portfolio-details ptb--120 bg_color--1">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="portfolio-details">
                <div className="section-title mb-xl-5 mb--30 text-center">
                  <span className="subtitle mb-xl-4">
                    {datasheet.showcase?.name ?? ""}
                  </span>
                  <h2 className="section-title text-center">
                    {datasheet.title ?? ""}
                  </h2>
                </div>

                {datasheet?.file?.[0]?.ext === ".gif" && (
                  <div className="image-gif">
                    <GifPlayer
                      gif={
                        siteUrl +
                        (datasheet?.file?.[0]?.url || "")
                      }
                      autoplay
                      className="image-gif-centered"
                    />
                  </div>
                )}

                <div className="row row--35 mb--50">
                  <div className="portfolio-thumb-inner mt-xl-5 mb-xl-5 w-100">
                    <div className="thumb mb--30">
                      {datasheet?.file &&
                      datasheet.file?.[0] !== null &&
                      datasheet?.file?.[0]?.ext !== ".pdf" &&
                      datasheet?.file?.[0]?.ext !== ".gif" &&
                      archiveCollectionName !== "video" ? (
                        <ImagesCarouselTwo images={datasheet.file} />
                      ) : (
                        ""
                      )}

                      {datasheet?.img &&
                      datasheet.img !== "" &&
                      datasheet.file?.[0] &&
                      datasheet?.file?.[0]?.ext !== ".jpg" &&
                      archiveCollectionName !== "video" ? (
                        <Image
                          type="standardSmall"
                          fileUrl={datasheet.url ?? ""}
                          imgUrl={imgSize(
                            { ...datasheet, img: { url: datasheet.img } },
                            isMobileOnly
                          )}
                        />
                      ) : (
                        ""
                      )}

                      {datasheet?.url &&
                        datasheet.url !== "" &&
                        archiveCollectionName === "video" && (
                          <VideoStandard
                            url={datasheet.url}
                            vidImg={
                              videosVimeoObject[
                                datasheet.url.replace("https://vimeo.com/", "")
                              ] ||
                              (datasheet.contentType?.image?.url
                                ? mediaUrl(datasheet.contentType.image.url)
                                : undefined)
                            }
                          />
                        )}

                      {Object.values(datasheet).length !== 0 &&
                        datasheet.url &&
                        datasheet.url !== "" &&
                        !datasheet?.file?.[0] &&
                        getFileExtension(datasheet.url) !== "mp3" &&
                        archiveCollectionName !== "video" && (
                          <div className="text-center border border-dark">
                            <iframe
                              src={datasheet.url}
                              title="File Viewer"
                              width="100%"
                              height="1100px"
                              style={{ border: "none" }}
                            />
                          </div>
                        )}

                      {Object.values(datasheet).length !== 0 &&
                        datasheet?.file?.[0]?.ext === ".pdf" &&
                        archiveCollectionName !== "video" && (
                          <div className="text-center border border-dark">
                            <iframe
                              src={
                                siteUrl +
                                (datasheet.file[0].url || "")
                              }
                              title="File Viewer"
                              width="100%"
                              height="1100px"
                              style={{ border: "none" }}
                            />
                          </div>
                        )}

                      {Object.values(datasheet).length !== 0 &&
                        datasheet?.url &&
                        datasheet.url !== "" &&
                        getFileExtension(datasheet.url) === "mp3" &&
                        archiveCollectionName !== "video" && (
                          <div className="container text-center">
                            <div className="row justify-content-center">
                              <div className="col-md-8">
                                <AudioPlayer
                                  src={datasheet.url}
                                  className="w-100"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>

                  {getArchiveDetailFields(datasheet).map((item, index) => {
                    if (index > 7 && item.text) {
                      counter += 1;
                      return (
                        <React.Fragment key={item.heading + index}>
                          {isOddAndInRange(counter, 1, 29) && (
                            <div className="col-lg-4">
                              <div className="port-view mt--20" />
                            </div>
                          )}

                          {item?.text && item?.heading === "Citation" ? (
                            <>
                              <div className="col-lg-4">
                                <div className="port-view mt--20" />
                              </div>
                              <div className="col-lg-4">
                                <div className="port-view mt--20" />
                              </div>
                              {renderDataItem(
                                item.heading,
                                item.text,
                                item.resources,
                                Number(item.colSize) || 4,
                                index
                              )}
                            </>
                          ) : (
                            renderDataItem(
                              item.heading,
                              item.text,
                              item.resources,
                              Number(item.colSize) || 4,
                              index
                            )
                          )}
                        </React.Fragment>
                      );
                    }

                    if (item?.text) {
                      return renderDataItem(
                        item.heading,
                        item.text,
                        item.resources,
                        Number(item.colSize) || 4,
                        index
                      );
                    }

                    return null;
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: "550px", width: "100%" }}>
        {Object.values(viewport).length !== 0 &&
        viewport?.longitude &&
        viewport?.latitude ? (
          <Map
            {...viewport}
            mapboxAccessToken={accessToken}
            mapStyle="mapbox://styles/mapbox/outdoors-v11"
            onMove={(evt) => setViewport(evt.viewState)}
            dragPan={false}
            dragRotate={false}
            scrollZoom={false}
            keyboard={false}
            doubleClickZoom={false}
            style={{ width: "100%", height: "100%" }}
          />
        ) : (
          ""
        )}
      </div>

      <div className="backto-top">
        <ScrollToTop showUnder={160}>
          <FiChevronUp />
        </ScrollToTop>
      </div>

      <Footer />
    </>
  );
};

export default memo(ArchiveDetails);
